import React, { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { X, ArrowRight, Eye, EyeOff } from "lucide-react";

export default function AuthModal() {
  const { showAuthModal, authModalContext, dismissAuthModal, login, register } = useAuth();
  const [mode, setMode] = useState("register");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (!showAuthModal) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    let result;
    if (mode === "login") {
      result = await login(email, password);
    } else {
      if (!name.trim()) { setError("Name is required"); setLoading(false); return; }
      if (password.length < 6) { setError("Password must be at least 6 characters"); setLoading(false); return; }
      result = await register(name, email, password);
    }
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  const handleDismiss = () => {
    dismissAuthModal();
    setError("");
    setName("");
    setEmail("");
    setPassword("");
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center" data-testid="auth-modal">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-earth-800/40" onClick={handleDismiss} />

      {/* Modal / Bottom Sheet */}
      <div className="relative w-full sm:max-w-md bg-white rounded-t-[2rem] sm:rounded-[2rem] p-8 shadow-2xl animate-fade-in-up z-10" data-testid="auth-modal-content">
        <button onClick={handleDismiss} className="absolute top-4 right-4 text-earth-400 hover:text-earth-600" data-testid="auth-modal-dismiss">
          <X size={20} />
        </button>

        {/* Context message */}
        <div className="text-center mb-6">
          <h3 className="font-heading text-2xl text-earth-800 mb-1">{authModalContext}</h3>
          <p className="text-earth-500 text-sm">Create a free account to save your data across sessions.</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-earth-100 rounded-full p-1 mb-6">
          <button onClick={() => { setMode("register"); setError(""); }} data-testid="modal-register-tab"
            className={`flex-1 py-2 rounded-full text-sm font-medium transition-all duration-300 ${mode === "register" ? "bg-white text-earth-800 shadow-sm" : "text-earth-500"}`}>
            Create account
          </button>
          <button onClick={() => { setMode("login"); setError(""); }} data-testid="modal-login-tab"
            className={`flex-1 py-2 rounded-full text-sm font-medium transition-all duration-300 ${mode === "login" ? "bg-white text-earth-800 shadow-sm" : "text-earth-500"}`}>
            Sign in
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          {mode === "register" && (
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name"
              data-testid="modal-name-input" autoComplete="name"
              className="bg-earth-100 border-0 rounded-2xl px-5 py-3 text-earth-800 w-full focus:ring-2 focus:ring-terracotta/30 focus:outline-none placeholder:text-earth-400 text-sm" />
          )}
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="your@email.com"
            data-testid="modal-email-input" autoComplete="email" required
            className="bg-earth-100 border-0 rounded-2xl px-5 py-3 text-earth-800 w-full focus:ring-2 focus:ring-terracotta/30 focus:outline-none placeholder:text-earth-400 text-sm" />
          <div className="relative">
            <input type={showPw ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Password (min 6 chars)"
              data-testid="modal-password-input" required
              className="bg-earth-100 border-0 rounded-2xl px-5 py-3 text-earth-800 w-full focus:ring-2 focus:ring-terracotta/30 focus:outline-none placeholder:text-earth-400 text-sm pr-12" />
            <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-4 top-3 text-earth-400">
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>

          {error && (
            <div className="bg-terracotta-light rounded-xl px-4 py-2" data-testid="modal-auth-error">
              <p className="text-terracotta text-sm">{error}</p>
            </div>
          )}

          <button type="submit" disabled={loading} data-testid="modal-auth-submit"
            className="w-full bg-terracotta text-earth-50 rounded-full py-3 font-medium hover:-translate-y-0.5 hover:shadow-lg transition-all duration-300 flex items-center justify-center gap-2 disabled:opacity-50">
            {loading ? "Please wait..." : mode === "register" ? "Create account" : "Sign in"}
            {!loading && <ArrowRight size={16} />}
          </button>
        </form>

        <button onClick={handleDismiss} data-testid="auth-modal-skip"
          className="w-full text-center mt-4 text-earth-400 text-sm hover:text-earth-600 transition-colors">
          Not now — continue without saving
        </button>
      </div>
    </div>
  );
}
